// scheduling/hooks/useSchedulerData.js
// The dashboard's single, deduped data layer. The three legacy dashboards
// (Unified badges + Manager + Staff) each fetched overlapping data independently
// — an Overview→My-Backlog session fired ~25+ /api/schedData calls where ~10 unique
// would do. This hook fetches the superset ONCE (matching what ManagerDashboard
// already pulled firm-wide, plus the My-Time inputs) and hands raw arrays to the
// presentational views, which keep their own client-side derivation memos. Because
// every derivation is a staffId/jobId-keyed lookup, firm-wide data + client-side
// filtering is equivalent to the old per-view scoped fetches.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  fetchStaff, fetchJobs, fetchSchedule, fetchDependencies, fetchJobsWIP,
  fetchCalendars, fetchPTO, fetchHolidays, fetchGlobalAssignments,
  fetchNotifications, fetchJobScheduledStaff,
} from '../services/schedData';
import { fetchOverBudget } from '../services/obData';
import { makeGetDayCap, makeGetContract, buildPtoByStaffDay, buildHolidayByDate, buildDayHoursByStaff } from '../utils/capacity';
import { resolveMe } from './useMe';

const EMPTY = [];

export function useSchedulerData() {
  const { ready, email, username } = useAuth();

  const [staff, setStaff] = useState(EMPTY);
  const [jobs, setJobs] = useState(EMPTY);
  const [schedule, setSchedule] = useState(EMPTY);
  const [jobsWIP, setJobsWIP] = useState(EMPTY);
  const [deps, setDeps] = useState(EMPTY);
  const [calendars, setCalendars] = useState(EMPTY);
  const [pto, setPto] = useState(EMPTY);
  const [holidays, setHolidays] = useState(EMPTY);
  const [globalAssignments, setGlobalAssignments] = useState(EMPTY);
  const [notifications, setNotifications] = useState(EMPTY);
  const [overBudget, setOverBudget] = useState(null);
  const [myTimeWIP, setMyTimeWIP] = useState(EMPTY);
  const [myTimeSched, setMyTimeSched] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // "me" resolves once staff loads; the me-scoped fetches key off it.
  const me = useMemo(() => resolveMe(staff, email, username), [staff, email, username]);
  const meStaffId = me ? me.StaffId : null;
  const meName = me ? (me.Name || me.StaffName || null) : null;

  // Firm-wide base data — fetched ONCE when auth is ready. Intentionally keyed on
  // [ready] only (NOT email/username): SWA sometimes settles the email after `ready`
  // flips, and we don't want that to re-pull the large firm-wide schedule/jobs. "me"
  // resolves reactively from the loaded staff (below); the me-scoped fetches live in
  // their own effect keyed on meStaffId.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setError(null);
        const [jobData, schedData, staffData, depData, wipData, calData, ptoData, holidayData, gaData, obData] = await Promise.all([
          fetchJobs({}),
          fetchSchedule({}),
          fetchStaff(),
          fetchDependencies(null),
          fetchJobsWIP({}),
          fetchCalendars(),
          fetchPTO({}),
          fetchHolidays(),
          fetchGlobalAssignments(),
          fetchOverBudget().catch(() => null),
        ]);
        if (cancelled) return;
        setJobs(Array.isArray(jobData) ? jobData : EMPTY);
        setSchedule(Array.isArray(schedData) ? schedData : EMPTY);
        setStaff(Array.isArray(staffData) ? staffData : EMPTY);
        setDeps(Array.isArray(depData) ? depData : EMPTY);
        setJobsWIP(Array.isArray(wipData) ? wipData : EMPTY);
        setCalendars(Array.isArray(calData) ? calData : EMPTY);
        setPto(Array.isArray(ptoData) ? ptoData : EMPTY);
        setHolidays(Array.isArray(holidayData) ? holidayData : EMPTY);
        setGlobalAssignments(Array.isArray(gaData) ? gaData : EMPTY);
        setOverBudget(obData && typeof obData === 'object' ? obData : null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, reloadKey]);

  // Me-scoped data (Overview notifications + the My Time backlog inputs) — fetched
  // once "me" resolves (small, cheap). Keyed on meStaffId so it never re-pulls the
  // firm-wide base above when identity settles.
  useEffect(() => {
    if (meStaffId == null) { setNotifications(EMPTY); setMyTimeWIP(EMPTY); setMyTimeSched(EMPTY); return undefined; }
    let cancelled = false;
    (async () => {
      const [notifs, myWip, mySched] = await Promise.all([
        fetchNotifications(meStaffId).catch(() => []),
        fetchJobsWIP({ recentForStaffId: meStaffId, recentDays: 30 }).catch(() => []),
        fetchJobScheduledStaff({ recentForStaffId: meStaffId, recentDays: 30 }).catch(() => []),
      ]);
      if (cancelled) return;
      setNotifications(Array.isArray(notifs) ? notifs : EMPTY);
      setMyTimeWIP(Array.isArray(myWip) ? myWip : EMPTY);
      setMyTimeSched(Array.isArray(mySched) ? mySched : EMPTY);
    })();
    return () => { cancelled = true; };
  }, [meStaffId, reloadKey]);

  // Shared capacity helper (PTO / holiday / recurring-season aware) — built once
  // from the firm-wide calendars/PTO/holidays and reused by the utilization stat
  // (My Schedule) + the overbooking conflict scan (Overview).
  const getDayCap = useMemo(() => {
    const years = new Set();
    for (const e of schedule) { const y = Number(String(e.EntryDate).slice(0, 4)); if (y) years.add(y); }
    const nowY = new Date().getFullYear();
    years.add(nowY); years.add(nowY + 1);
    return makeGetDayCap({
      staff,
      globalCalendars: calendars,
      ptoByStaffDay: buildPtoByStaffDay(pto),
      holidayByDate: buildHolidayByDate(holidays, years),
    });
  }, [staff, calendars, pto, holidays, schedule]);

  const dayHoursByStaff = useMemo(
    () => buildDayHoursByStaff(schedule, globalAssignments, staff),
    [schedule, globalAssignments, staff],
  );

  // Each staffer's contract (FT / PT / Flex / Leave) for the chips on Roll Forward and
  // the schedule chooser — same templates as getDayCap, so the label and the capacity
  // math can never disagree.
  const getContract = useMemo(
    () => makeGetContract({ staff, globalCalendars: calendars }),
    [staff, calendars],
  );

  // Full reload (re-run every fetch) and a lighter schedule-only refresh for
  // after a write from the Schedule modal / roll-forward.
  const reload = useCallback(() => setReloadKey(k => k + 1), []);
  const refreshSchedule = useCallback(async () => {
    try {
      const s = await fetchSchedule({});
      setSchedule(Array.isArray(s) ? s : EMPTY);
    } catch { /* ignore */ }
  }, []);
  const refreshDeps = useCallback(async () => {
    try {
      const d = await fetchDependencies(null);
      setDeps(Array.isArray(d) ? d : EMPTY);
    } catch { /* ignore */ }
  }, []);
  // Optimistic single-job patch (used by the On-site toggle).
  const patchJob = useCallback((jobId, partial) => {
    setJobs(prev => prev.map(j => (j.JobId === jobId ? { ...j, ...partial } : j)));
  }, []);

  return {
    // identity
    me, meStaffId, meName,
    // raw data
    staff, jobs, schedule, jobsWIP, deps, calendars, pto, holidays,
    globalAssignments, notifications, overBudget, myTimeWIP, myTimeSched,
    // shared derived
    getDayCap, dayHoursByStaff, getContract,
    // status + actions
    loading, error, reload, refreshSchedule, refreshDeps, patchJob,
  };
}
